/*
* Copyright (c) 2017 Honeywell International, Inc. All rights reserved.
*
* This file contains trade secrets of Honeywell International, Inc.  No part
*  may be reproduced or transmitted in any form by any means or for any
*  purpose without the express written permission of Honeywell.
*
*/

import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import { Asset } from '../models';
import { Point } from '../models/point.model';
import { environment } from '../../environments/environment';
import { HttpParams } from '@angular/common/http';


@Injectable()
export class AssetService {
    api_url: any;

    constructor(private apiService: ApiService) {
        this.api_url = environment.cdt.api_url;
    }

    confirm(siteId: String, assets: Array<Asset>): Observable<any> {
        const reducedAssets = assets.map(this.reduceAsset);
        return this.apiService.post('sites/' + siteId + '/assets/confirm', this.api_url, JSON.stringify(reducedAssets));
    }

    unconfirm(siteId: String, assets: Array<Asset>): Observable<any> {
        const reducedAssets = assets.map(this.reduceAsset);
        return this.apiService.post('sites/' + siteId + '/assets/unconfirm', this.api_url, JSON.stringify(reducedAssets));
    }

    regroup(siteId: String, equipmentIri: String, assets: Array<Asset>) {
        const reducedAssets = assets.map(this.reduceAsset);
        return this.apiService.post('sites/' + siteId + '/assets/regroup', this.api_url, JSON.stringify({
            assets: reducedAssets,
            equipmentIri: equipmentIri
        }));
    }

    rename(siteId: String, assetIri: String, newLabel: String) {
        return this.apiService.post('sites/' + siteId + '/assets/rename', this.api_url, JSON.stringify({
            assetIri: assetIri,
            assetLabel: newLabel
        }));
    }

    create(siteId: String, equipmentIri: String, assetname: String) {
        return this.apiService.post('sites/' + siteId + '/assets/create', this.api_url, JSON.stringify({
            equipmentIri: equipmentIri,
            assetName: assetname
        }));
    }

    bulkCreateAssets(siteId: string, assets: Array<Asset>) {
        return this.apiService.post('sites/' + siteId + '/assets/bulkcreate', this.api_url, JSON.stringify(assets));
    }

    private mapUpstream(siteId: String, assetIri: String, upstreamAssetsIri: String[]): Observable<any> {
        return this.apiService.post('sites/' + siteId + '/assets/map-upstream', this.api_url, JSON.stringify({
            assetIri: assetIri,
            upstreamAssetsIri: upstreamAssetsIri
        }));
    }

    private reduceAsset(asset: Asset) {
        if (asset.points != null) {
            asset.points = asset.points.map(point => {
                const newPoint = new Point();
                newPoint.iri = point.iri;
                return newPoint;
            });
        }
        return asset;
    }

    private mapDownstream(siteId: String, assetIri: String, downstreamAssetsIri: String[]): Observable<any> {
        return this.apiService.post('sites/' + siteId + '/assets/map-downstream', this.api_url, JSON.stringify({
            assetIri: assetIri,
            downstreamAssetsIri: downstreamAssetsIri
        }));
    }

    mapRelationship(siteId: String, assetIri: String, relatedAssetIris: String[], isUpstream: boolean) {
        return isUpstream ? this.mapUpstream(siteId, assetIri, relatedAssetIris)
            : this.mapDownstream(siteId, assetIri, relatedAssetIris);
    }

    private unmapUpstream(siteId: String, assetIri: String, upstreamAssetsIri: String[]): Observable<any> {
        return this.apiService.post('sites/' + siteId + '/assets/unmap-upstream', this.api_url, JSON.stringify({
            assetIri: assetIri,
            upstreamAssetsIri: upstreamAssetsIri
        }));
    }

    private unmapDownstream(siteId: String, assetIri: String, downstreamAssetsIri: String[]): Observable<any> {
        return this.apiService.post('sites/' + siteId + '/assets/unmap-downstream', this.api_url, JSON.stringify({
            assetIri: assetIri,
            downstreamAssetsIri: downstreamAssetsIri
        }));
    }

    unmapRelationship(siteId: String, assetIri: String, selectedAssetIris: String[], isUpstream: boolean) {
        return isUpstream ? this.unmapUpstream(siteId, assetIri, selectedAssetIris)
            : this.unmapDownstream(siteId, assetIri, selectedAssetIris);
    }

    getRelatedAssets(siteId: string, assetIri: string, isUpstream: boolean): Observable<[Asset]> {
        let params = new HttpParams();
        params = params.set('assetIri', assetIri);
        const assetsUrl = isUpstream ? 'sites/' + siteId + '/assets/upstream' : 'sites/' + siteId + '/assets/downstream';
        return this.apiService.get(assetsUrl, this.api_url, params);
    }

    listAssets(siteId: string, equipmentIri: string): Observable<[Asset]> {
        let params = new HttpParams();
        params = params.set('equipmentIri', equipmentIri);
        return this.apiService.get('sites/' + siteId + '/assets', this.api_url, params);
    }
    listAllAssets(siteId: string): Observable<[Asset]> {
        return this.apiService.get('sites/' + siteId + '/assets', this.api_url);
    }

    getMappableAssets(siteId: string, equipmentIri: string, assetIri: string, isUpstream: boolean): Observable<[Asset]> {
        let params = new HttpParams();
        params = params.set('assetIri', assetIri);
        const equipmentQueryParamKey = isUpstream ? 'upstreamEquipmentIri' : 'downstreamEquipmentIri';
        params = params.set(equipmentQueryParamKey, equipmentIri);
        const urlPart = isUpstream ? 'mappable-upstream' : 'mappable-downstream';
        return this.apiService.get('sites/' + siteId + '/assets/' + urlPart, this.api_url, params);
    }
}
